import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateAdyenPaymentSettingsDocument,
    type CreateAdyenPaymentSettingsMutation,
    type CreateAdyenPaymentSettingsMutationVariables,
} from '../../../../api/mutations/createAdyenPaymentSettings';
import { type AdyenPaymentSettingsInput, Environment } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputFieldArray from '../../../../components/fields/InputFieldArray';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import useEnvironmentOptions from '../modules/implementations/shared/useEnvironmentOptions';
import validator from './validators';

export type AddAdyenPaymentSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = AdyenPaymentSettingsInput;

const initialValues: FormValues = {
    amount: 0,
    apiKey: '',
    clientKey: '',
    currency: '',
    displayName: '',
    environment: Environment.Test,
    hmacKeys: [],
    merchantAccount: '',
    password: '',
    username: '',
};

const AddAdyenPaymentSettingsForm = ({ moduleId, onComplete }: AddAdyenPaymentSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('adyenPaymentModuleDetails:paymentSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateAdyenPaymentSettingsMutation, CreateAdyenPaymentSettingsMutationVariables>({
                    mutation: CreateAdyenPaymentSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('adyenPaymentModuleDetails:paymentSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator);
    const environmentOptions = useEnvironmentOptions();

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addAdyenPaymentSettings"
                    layout="vertical"
                    name="addAdyenPaymentSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('adyenPaymentModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                            returnObjects: true,
                        })}
                        name="currency"
                    />
                    <InputNumberField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.amount', { returnObjects: true })}
                        name="amount"
                    />
                    <Divider>{t('adyenPaymentModuleDetails:dividers.paymentSettings')}</Divider>
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.merchant', {
                            returnObjects: true,
                        })}
                        name="merchantAccount"
                    />
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', { returnObjects: true })}
                        name="apiKey"
                    />
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.clientKey', {
                            returnObjects: true,
                        })}
                        name="clientKey"
                    />
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.liveEndpointUrlPrefix', {
                            returnObjects: true,
                        })}
                        name="liveEndpointUrlPrefix"
                    />
                    <SelectField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.environment', {
                            returnObjects: true,
                        })}
                        name="environment"
                        options={environmentOptions}
                        showSearch
                    />
                    <Divider>{t('adyenPaymentModuleDetails:dividers.notificationSettings')}</Divider>
                    <InputFieldArray
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.hmacKeys', {
                            returnObjects: true,
                        })}
                        labelForAdd={t('adyenPaymentModuleDetails:paymentSettingsDrawer.addHmacKeyButton')}
                        name="hmacKeys"
                    />
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.username', {
                            returnObjects: true,
                        })}
                        name="username"
                    />
                    <PasswordField
                        {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.password', {
                            returnObjects: true,
                        })}
                        name="password"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddAdyenPaymentSettingsForm;
